import { useId, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import type { BajaCampana, HorarioCampana } from "../../types";
import { textoFranjas, textoHorario, type HorarioEnEdicion } from "../../logic/horario";
import { motivoBajaNoDisponible, type EleccionBaja } from "../../logic/baja";
import { ZONAS, ZONA_MARCA, etiquetaZona, paisDeZona } from "../../logic/zonas";
import { EditorFranjas } from "../EditorFranjas";

const OPCIONES_BAJA: EleccionBaja[] = ["boton", "texto", "los_dos", "ninguna"];

/** «con el botón «No quiero más»», «respondiendo BAJA»…: lo que se eligió, en palabras. */
export function textoDeLaBaja(e: EleccionBaja, botonBaja?: string | null): string {
  const boton = `«${String(botonBaja || "").trim() || "No quiero más"}»`;
  return e === "boton" ? `con el botón ${boton}` : e === "texto" ? "respondiendo BAJA" : e === "los_dos" ? `con el botón ${boton} o respondiendo BAJA` : "sin forma de darse de baja";
}

/** «la franja de la marca (…)» o la propia, con el tope y el último día si los tiene. */
export function textoDelHorario(h: HorarioCampana | null, franjasCampana: string | null, franjasMarca: string): string {
  const resto = h ? textoHorario({ ...h, franjas: null }, null) : "";
  // con otra zona, cada franja dice en qué hora va (la del backend ya lo trae)
  const franja = h?.franjas?.length
    ? `franja propia: ${franjasCampana || textoFranjas(h.franjas) + (h.zona ? ` (hora de ${paisDeZona(h.zona)})` : "")}`
    : `la franja de la marca (${franjasMarca || "—"}${h?.zona ? `, hora de ${paisDeZona(ZONA_MARCA)}` : ""})`;
  return resto ? `${franja} · ${resto.charAt(0).toLowerCase()}${resto.slice(1)}` : franja;
}

const botonCambiar = "h-11 flex-shrink-0 rounded-lg px-3 text-sm font-semibold text-violet-700 hover:bg-violet-50";

/**
 * «Cuándo», el tercer paso (29/10/2026; antes «Horario y baja», 26/10: «cada campaña tiene su franja específica y su
 * método de baja»). Arriba, «Ahora» o «Programar» (`elegirCuando`, sin ninguno marcado). Después, lo que ya se sabe,
 * puesto y a la vista, cada cosa con su «Cambiar»: la franja de la marca y la baja que trae la plantilla. El tope por
 * día, el último día y la zona horaria van en «Más opciones». Todo se guarda con «Siguiente», con lo que se ve.
 *
 * La baja tiene que estar en la plantilla: la campaña no le agrega nada a lo que aprobó Meta. Si la plantilla no trae
 * ninguna, las opciones quedan abiertas y se dice que hay que elegir.
 */
export function PasoCuando(p: {
  /** «Ahora» o «Programar» (`ElegirCuando`) */
  elegirCuando: ReactNode;
  /** el estado en vivo de la plantilla mientras Meta la revisa (null si ya está aprobada) */
  estadoEnVivo: ReactNode;
  horarioEd: HorarioEnEdicion; onHorario: (f: (h: HorarioEnEdicion) => HorarioEnEdicion) => void;
  onElegirFranja: (conFranja: boolean) => void;
  errores: string[]; limiteMeta: number | null | undefined; minHasta: string; franjasMarcaTexto: string;
  /** la baja que se ve: la elegida o, si no hay, la que trae la plantilla ("" = hay que elegirla) */
  baja: EleccionBaja | ""; onBaja: (e: EleccionBaja) => void;
  /** lo que trae la plantilla elegida (null mientras carga) */
  trae: BajaCampana | null; botonBaja?: string | null;
  pideOptOut: boolean; onConfirmarOptOut: () => void; confirmandoOptOut: boolean; errorOptOut: string; rutaConfig: string;
}) {
  const h = p.horarioEd;
  const idMas = useId();
  const [cambiarFranja, setCambiarFranja] = useState(h.conFranja);
  const [cambiarBaja, setCambiarBaja] = useState(false);
  // abierto si ya tiene algo distinto de lo de siempre: que no quede escondido lo que va a cambiar el envío
  const [masOpciones, setMasOpciones] = useState(!!h.tope || !!h.hasta || h.zona !== ZONA_MARCA);
  const bajaNoVa = p.baja && p.trae ? motivoBajaNoDisponible(p.baja, p.trae, p.botonBaja) : "";
  const verOpcionesBaja = cambiarBaja || !p.baja || !!bajaNoVa;
  const deOtraZona = h.zona !== ZONA_MARCA ? ` (hora de ${paisDeZona(h.zona)})` : "";

  return (
    <div className="space-y-6">
      {p.estadoEnVivo}
      {p.elegirCuando}

      <section aria-label="Horario" className="space-y-3">
        <div className="flex items-center justify-between gap-3 rounded-lg bg-slate-50 px-3 py-1 text-sm text-slate-700" data-testid="cuando-horario">
          <p className="py-2">
            {h.conFranja
              ? `Sale en una franja propia: ${textoFranjas(h.franjas) || "—"}${deOtraZona}.`
              : `Sale de ${p.franjasMarcaTexto || "—"}, la franja de tu marca (hora de ${paisDeZona(ZONA_MARCA)}).`}
          </p>
          {!cambiarFranja && <button type="button" onClick={() => setCambiarFranja(true)} className={botonCambiar} aria-label="Cambiar el horario">Cambiar</button>}
        </div>
        {cambiarFranja && (
          <fieldset className="space-y-3">
            <legend className="text-sm font-semibold text-slate-800">¿En qué horario sale?</legend>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input type="radio" name="franja" checked={!h.conFranja} onChange={() => p.onElegirFranja(false)} className="mt-0.5 accent-violet-600" />
              <span>Con la franja de la marca <span className="text-slate-500">({p.franjasMarcaTexto || "—"}, hora de {paisDeZona(ZONA_MARCA)})</span></span>
            </label>
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input type="radio" name="franja" checked={h.conFranja} onChange={() => p.onElegirFranja(true)} className="mt-0.5 accent-violet-600" />
              <span>Con una franja propia <span className="text-slate-500">(dentro de la franja de la marca)</span></span>
            </label>
            {h.conFranja && (
              <EditorFranjas franjas={h.franjas} onChange={(franjas) => p.onHorario((x) => ({ ...x, franjas }))} errores={[]} />
            )}
          </fieldset>
        )}
      </section>

      <section aria-label="Baja" className="space-y-3">
        <div className={`flex items-center justify-between gap-3 rounded-lg px-3 py-1 text-sm ${p.baja ? "bg-slate-50 text-slate-700" : "bg-amber-50 text-amber-900"}`} data-testid="cuando-baja">
          <p className="py-2">
            {!p.baja ? "La plantilla no trae cómo darse de baja (ni el botón ni BAJA): elegí qué hacer."
              : p.baja === "ninguna" ? "No tiene forma de darse de baja (no recomendado: la ley lo pide en toda publicidad)."
              : `Se dan de baja ${textoDeLaBaja(p.baja, p.botonBaja)}.`}
          </p>
          {!verOpcionesBaja && <button type="button" onClick={() => setCambiarBaja(true)} className={botonCambiar} aria-label="Cambiar la baja">Cambiar</button>}
        </div>
        {verOpcionesBaja && (
          <fieldset className="space-y-3">
            <legend className="text-sm font-semibold text-slate-800">¿Cómo se dan de baja quienes la reciben?</legend>
            {OPCIONES_BAJA.map((e) => {
              const motivo = p.trae ? motivoBajaNoDisponible(e, p.trae, p.botonBaja) : "";
              return (
                <label key={e} className={`flex items-start gap-2 text-sm ${motivo ? "text-slate-400" : "text-slate-700"}`}>
                  {/* elegida una, las opciones siguen abiertas: no se esconden debajo del dedo */}
                  <input type="radio" name="baja" value={e} checked={p.baja === e} disabled={!!motivo} onChange={() => { setCambiarBaja(true); p.onBaja(e); }} className="mt-0.5 accent-violet-600" />
                  <span>
                    {textoDeLaBaja(e, p.botonBaja).replace(/^./, (x) => x.toUpperCase())}
                    {e === "ninguna" && <span className="text-slate-500"> (no recomendado: la ley lo pide en toda publicidad)</span>}
                    {motivo && <span className="block text-xs">{motivo}</span>}
                  </span>
                </label>
              );
            })}
            <p className="text-xs text-slate-500">Lo que se elige tiene que estar en la plantilla. Si alguien escribe «baja», queda dado de baja igual.</p>
          </fieldset>
        )}
        {p.pideOptOut && (
          <div className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="confirmar-opt-out">
            <p>Para que BAJA dé de baja sola también en Twilio, el Messaging Service tiene que tener la baja automática con esa palabra.</p>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              <button type="button" onClick={p.onConfirmarOptOut} disabled={p.confirmandoOptOut}
                className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50">
                {p.confirmandoOptOut ? "Guardando…" : "Ya está configurada"}
              </button>
              <Link to={p.rutaConfig} className="text-xs font-semibold underline">Ver en Configuración</Link>
            </div>
            {p.errorOptOut && <p className="mt-1 text-xs text-red-700" role="alert">{p.errorOptOut}</p>}
          </div>
        )}
      </section>

      <div className="space-y-3">
        <button type="button" onClick={() => setMasOpciones((v) => !v)} aria-expanded={masOpciones} aria-controls={idMas}
          className="inline-flex h-11 items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-slate-900">
          <ChevronDown size={15} className={`transition-transform ${masOpciones ? "rotate-180" : ""}`} aria-hidden="true" /> Más opciones
        </button>
        {masOpciones && (
          <div id={idMas} className="space-y-4">
            {/* Alan, 26/10/2026: «que siempre se pueda poner si es horario argentino o de qué país». */}
            <label className="flex flex-col gap-1 text-sm text-slate-700 max-w-sm">
              Zona horaria de la campaña
              <select value={h.zona} onChange={(e) => p.onHorario((x) => ({ ...x, zona: e.target.value }))}
                className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500">
                {ZONAS.map((z) => <option key={z.id} value={z.id}>{etiquetaZona(z.id)}</option>)}
              </select>
              <span className="text-xs text-slate-500">
                La franja propia, el último día y la hora a la que se programa van en hora de {paisDeZona(h.zona)}.
                {h.zona !== ZONA_MARCA && ` La franja de la marca es en hora de ${paisDeZona(ZONA_MARCA)}.`}
              </span>
            </label>
            <div className="flex flex-wrap items-end gap-4">
              <label className="flex flex-col gap-1 text-sm text-slate-700">
                Máximo por día
                <input type="number" inputMode="numeric" min={1} max={p.limiteMeta ?? undefined} placeholder="Sin tope" value={h.tope}
                  onChange={(e) => p.onHorario((x) => ({ ...x, tope: e.target.value }))}
                  className="w-36 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
              </label>
              <label className="flex flex-col gap-1 text-sm text-slate-700">
                Último día
                <input type="date" value={h.hasta} min={p.minHasta}
                  onChange={(e) => p.onHorario((x) => ({ ...x, hasta: e.target.value }))}
                  className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
              </label>
            </div>
            <p className="text-xs text-slate-500">Sin máximo sale hasta el cupo de Meta. Lo que no salió el último día, no sale.</p>
          </div>
        )}
        {p.errores.map((e) => <p key={e} className="text-xs text-red-700" role="alert">{e}</p>)}
      </div>
    </div>
  );
}
